import { useMemo, useCallback, useEffect } from "react";
import { useVisitorInfo } from "@/common/hooks/useVisitorInfo";
import { showNotification } from "@/components/alerts/Notifications";
import { metricsBus, EventSource } from "@/common/buses";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { useFollowsStore } from "@/common/store/followsStore";
import { apiMain } from "../services";
import { isAuthenticated } from "../services/Auth";
import { useNavigate } from "react-router-dom";
import { routes } from "../utils/routes";

type TFollowContext = {
  gifId?: string;
  position?: number;
  playCount?: number;
}

export const useUserSubscription = (username: string, withMessage?: boolean) => {
  const currentUser = useVisitorInfo();
  const { navigateSignIn } = useAuthNavigate();
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const followingIds = useFollowsStore((state) => state.follows);
  const follow = useFollowsStore((state) => state.follow);
  const unfollow = useFollowsStore((state) => state.unfollow);
  const checkFollowing = useFollowsStore((state) => state.checkFollowing);
  const replaceAllFollows = useFollowsStore((state) => state.replaceAllFollows);
  const isFetched = useFollowsStore((state) => state.isFetched);
  const setFetched = useFollowsStore((state) => state.setFetched);
  const isLoading = useFollowsStore((state) => state.isLoading);
  const setLoading = useFollowsStore((state) => state.setLoading);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isFetched && !isLoading) {
      (async () => {
        setLoading(true);
        try {
          if (await isAuthenticated()) {
            const data = await apiMain.getFollowingIds();
            replaceAllFollows(data);
            setFetched(true);
          }
        } catch (error) {
          console.warn("Error fetching following IDs:", error);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  const subscribed = useMemo(() => checkFollowing(username), [followingIds, username]);

  const emitAnonymousFollowEvent = useCallback(() => {
    metricsBus.emit("followUserClickedAnonymously", {
      userName: username,
      source: eventSource?.name ?? EventSource.Other,
      sourceId: eventSource?.id ?? null
    });
  }, [username, eventSource]);

  const subscribe = useCallback(async (context: TFollowContext | undefined = {}) => {
    if (!currentUser) {
      emitAnonymousFollowEvent();
      if (withMessage) {
        navigate(routes.user(username), { state: { showSignUpWarning: true } });
      } else {
        navigateSignIn();
      }

      return;
    }

    follow(username);

    try {
      await apiMain.followUser(username, eventSource?.id ?? null);
      metricsBus.emit("followUserClicked", {
        userName: username,
        source: eventSource?.name ?? EventSource.Other,
        sourceId: eventSource?.id ?? null,
        ...context,
      });
    } catch (err) {
      unfollow(username);
      showNotification({
        title: `Could not follow ${username}`,
        variant: "error",
      });
    }
  }, [currentUser, follow, unfollow, emitAnonymousFollowEvent, navigateSignIn]);

  const unsubscribe = useCallback(async () => {
    if (!currentUser) {
      emitAnonymousFollowEvent();
      navigateSignIn();
      return;
    }

    unfollow(username);

    try {
      await apiMain.unfollowUser(username);
      metricsBus.emit("unfollowUserClicked", {
        userName: username,
        source: eventSource?.name ?? EventSource.Other,
        sourceId: eventSource?.id ?? null
      });
    } catch (err) {
      follow(username);
      showNotification({
        title: `Could not unfollow ${username}`,
        variant: "error",
      });
    }
  }, [currentUser, follow, unfollow, emitAnonymousFollowEvent, navigateSignIn]);

  return {
    subscribed,
    subscribe,
    unsubscribe
  };
};
